import React, { memo } from 'react';
import { HomefieldIconAlertInfoOutlined } from '@dsg-tech/homefield-react-icons';
import { Tooltip } from 'common/Tooltip/Tooltip';
import { calculateAddToCartPrice } from '../utility/utils';
import { SkuDetails } from './FulfillmentOptions/types';

type ProductPriceProps = {
  priceDetails: {
    maxlistprice: string;
    minlistprice: string;
    maxofferprice: string;
    minofferprice: string;
    mapPriceIndicator: number;
    priceIndicator: number;
  };
  catentryId: number;
  selectedSKU: SkuDetails | null;
};

const pricingText = {
  wasPriceAfter: '*',
  unlistedPrice: 'See Price In Cart',
};

const ProductPrice = ({ priceDetails, catentryId, selectedSKU }: ProductPriceProps) => {
  const renderListPrice = (minListPrice: string, maxListPrice: string) => {
    if (!minListPrice || !maxListPrice) return null;
    return (
      <p className="label-base-subtle hmf-body-s hmf-text-decoration-linethrough hmf-m-0">
        {`$${minListPrice}`}
        {maxListPrice !== minListPrice
          ? ` - $${maxListPrice}${pricingText.wasPriceAfter}`
          : pricingText.wasPriceAfter}
      </p>
    );
  };

  const renderOfferPrice = (minOfferPrice: string, maxOfferPrice: string, isClearance: boolean) => {
    if (!minOfferPrice || !maxOfferPrice) return null;

    const showPrice = selectedSKU
      ? `$${calculateAddToCartPrice(selectedSKU?.prices?.offerPrice, 1)}`
      : `$${minOfferPrice}`;

    return (
      <p
        className={`hmf-header-bold-s ${
          isClearance ? 'offer-price label-sale hmf-m-0 hmf-pr-xxxs' : 'final-price'
        }`}>
        {showPrice}
        {!selectedSKU && (maxOfferPrice !== minOfferPrice ? ` - $${maxOfferPrice}` : '')}
      </p>
    );
  };

  const renderSeeInCart = (minListPrice: string, maxListPrice: string) => {
    return (
      <>
        <p className="unlisted-price hmf-header-bold-m">{pricingText.unlistedPrice}</p>
        <Tooltip
          title="Why Don’t We Show The Price?"
          content="As a retailer, we set our own prices independently, but some manufacturers restrict how we may communicate those prices. In some cases the manufacturer does not allow us to show you our price until the item is in your cart."
          direction="bottom">
          <HomefieldIconAlertInfoOutlined width="24px" height="24px" />
        </Tooltip>

        {renderListPrice(minListPrice, maxListPrice)}
      </>
    );
  };

  const renderPrice = (
    minOfferPrice: string,
    minListPrice: string,
    maxOfferPrice: string,
    maxListPrice: string,
    isClearance: boolean,
  ) => {
    const offerPrice = selectedSKU
      ? calculateAddToCartPrice(selectedSKU?.prices?.offerPrice, 1)
      : minOfferPrice;
    const shouldRenderListPrice =
      !selectedSKU || (minListPrice !== offerPrice && maxListPrice !== offerPrice);

    return (
      <>
        {renderOfferPrice(minOfferPrice, maxOfferPrice, isClearance)}
        {isClearance && shouldRenderListPrice && renderListPrice(minListPrice, maxListPrice)}
      </>
    );
  };

  // map price indicator is 1 "see price in cart"
  // price indicator is 0 was price
  // offer price (min and max)
  // list price (was price) or not on sale price (min and max)
  if (!priceDetails) {
    return null;
  }
  const {
    maxlistprice,
    minlistprice,
    maxofferprice,
    minofferprice,
    mapPriceIndicator,
    priceIndicator,
  } = priceDetails;

  const seePriceInCart = mapPriceIndicator === 1;
  const isClearance = priceIndicator === 0;

  return (
    <div
      className=" hmf-display-flex hmf-align-items-center"
      key={`PrdPrice_${catentryId}`}
      id={`product_price_${catentryId}`}>
      {seePriceInCart
        ? renderSeeInCart(minlistprice, maxlistprice)
        : renderPrice(minofferprice, minlistprice, maxofferprice, maxlistprice, isClearance)}
    </div>
  );
};

export default memo(ProductPrice);
